import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { getThemeClass } from '../../lib/theme'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { supabase } from '../../lib/supabase'
// useAuth is not directly used in this component after refactor, but keeping for potential future use or if other parts depend on it.
// import { useAuth } from '../../lib/auth.context' 
import { TOTPEnrollment } from '../../components/auth/totp-enrollment'
import { processAuthLink, cleanupAuthLinkUrl } from '../../lib/auth-link-utils' // Removed getRedirectUrlBasedOnRole as it's not used with HEAD
import { getUserProfile } from '../../lib/api/api'
import { env } from '../../lib/env'

export function CompleteInvitePage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showTOTP, setShowTOTP] = useState(false)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [redirectTo, setRedirectTo] = useState('/admin/dashboard')
  const navigate = useNavigate()
  const { toast } = useToast()
  // const { getToken } = useAuth(); // getToken is not used directly here anymore

  // Handle auth link processing when component mounts
  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[CompleteInvitePage] useEffect - handleAuthFlow called');
      setIsLoading(true); // Set loading while processing the link

      try {
        // Pass `location` from `window.location`
        const result = await processAuthLink(supabase, window.location, 'invite');
        
        if (result.success && result.session) {
          console.log('[CompleteInvitePage] Successfully authenticated via processAuthLink. Session:', result.session);
          setHasAccessToken(true);
          cleanupAuthLinkUrl(window.location, false); // Clear hash and 'code' param
        } else {
          console.log('[CompleteInvitePage] Authentication failed via processAuthLink. Error:', result.error);
          toast({
            title: 'Error',
            description: result.error?.message || 'Invalid or expired invite link. Please request a new invitation.',
            variant: 'destructive',
          });
          navigate('/auth/login', { replace: true });
        }
      } catch (error) { // Catch unexpected errors from processAuthLink
        console.error('[CompleteInvitePage] Unexpected error in handleAuthFlow:', error);

        toast({
          title: 'Error',
          description: 'Failed to process invite link. Please try again.',
          variant: 'destructive',
        });
        navigate('/auth/login', { replace: true });
      } finally {
        setIsLoading(false); // Clear loading state
      }
    };

    handleAuthFlow();
  }, [navigate, toast, supabase]) // Added supabase to dependencies

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    // Validate passwords match
    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    // Validate password length
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long')
      return
    }

    setIsLoading(true)
    try {
      // Get the current session to verify it's still valid
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Your session has expired. Please use a new invite link.')
      }

      // Update the password
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        throw error
      }

      toast({
        title: 'Password Set',
        description: 'Password has been set successfully. Please complete the security setup by configuring two-factor authentication.',
      })

      // Get user profile to check role
      const userProfile = await getUserProfile()
      const isClubUser = userProfile.userRole?.startsWith('entity_')

      // Set the appropriate redirect URL based on user role
      const newRedirectTo = isClubUser && env.VITE_USER_DASHBOARD_URL
        ? env.VITE_USER_DASHBOARD_URL
        : '/admin/dashboard'

      // If user is a club user, store the access token before redirecting
      if (isClubUser && env.VITE_USER_DASHBOARD_URL) {
        // Store the access token in localStorage for club-ui
        localStorage.setItem('club_access_token', session.access_token)
      }

      // All users must complete TOTP enrollment
      setRedirectTo(newRedirectTo)
      setShowTOTP(true)
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to set password'
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })

    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`flex min-h-screen items-center justify-center ${getThemeClass('gradients.primary')}`}>
      {!showTOTP ? (
        <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
          <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>Complete Your Registration</h1>
          {!hasAccessToken ? (
            <Alert variant="destructive">
              <AlertDescription>Invalid invite link. Please request a new invitation.</AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className={getThemeClass('components.input.base')}
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className={getThemeClass('components.input.base')}
                />
              </div>
              {formError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}
              <Button
                type="submit"
                className={`w-full ${getThemeClass('components.button.primary')}`}
                disabled={isLoading}
              >
                {isLoading ? 'Setting Password...' : 'Set Password'}
              </Button>
            </form>
          )}
        </Card>
      ) : (
        <TOTPEnrollment redirectTo={redirectTo} />
      )}
    </div>
  )
}
